import React, { useEffect, useState } from 'react';
import { api as apiClient } from '../../lib/api';
import type { DashboardStatsDto, Workspace } from '@sahaj/types';
import { Building2, Users, AlertCircle, ShieldCheck, Calendar, MoreVertical } from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';

const activityData = [
  { name: '21 Aug', active: 5, new: 1 },
  { name: '22 Aug', active: 6, new: 2 },
  { name: '23 Aug', active: 4, new: 1 },
  { name: '24 Aug', active: 8, new: 3 },
  { name: '25 Aug', active: 7, new: 2 },
  { name: '26 Aug', active: 9, new: 2 },
  { name: '27 Aug', active: 8, new: 1 },
];

const sparklineData1 = [{value: 3}, {value: 4}, {value: 3}, {value: 5}, {value: 4}, {value: 6}, {value: 7}];
const sparklineData2 = [{value: 4}, {value: 3}, {value: 5}, {value: 4}, {value: 6}, {value: 5}, {value: 7}];
const sparklineData3 = [{value: 1}, {value: 1}, {value: 0}, {value: 0}, {value: 0}, {value: 0}, {value: 0}];
const sparklineData4 = [{value: 10}, {value: 12}, {value: 11}, {value: 14}, {value: 13}, {value: 16}, {value: 15}];

export const PlatformDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStatsDto | null>(null);
  const [recentWorkspaces, setRecentWorkspaces] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, workspacesData] = await Promise.all([
          apiClient.platform.getDashboardStats(),
          apiClient.platform.getWorkspaces(0, 5)
        ]);
        setStats(statsData);
        setRecentWorkspaces(workspacesData.content);
      } catch (error) {
        console.error('Failed to fetch dashboard data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading || !stats) {
    return (
      <div className="p-8 h-full flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#6366F1] border-t-transparent rounded-full" />
      </div>
    );
  }

  const donutData = [
    { name: 'Active', value: stats.activeWorkspaces, color: '#10B981' },
    { name: 'Disabled', value: stats.disabledWorkspaces, color: '#EF4444' },
    { name: 'Archived', value: 0, color: '#9CA3AF' },
  ];

  return (
    <div className="w-full max-w-[1400px] mx-auto font-sans text-foreground bg-transparent">
      <div className="flex items-center justify-end mb-4">
        <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-xl bg-card text-[13px] font-bold text-muted-foreground hover:bg-muted transition shadow-xs">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          20 Aug 2026 - 27 Aug 2026
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          title="Total Workspaces"
          value={stats.totalWorkspaces}
          icon={<Building2 className="w-5 h-5" />}
          trend="↑ 3 this week"
          trendColor="text-emerald-500"
          iconBg="bg-primary/10"
          iconColor="text-primary"
          sparklineData={sparklineData1}
          sparklineColor="#818CF8"
        />
        <StatCard
          title="Active Workspaces"
          value={stats.activeWorkspaces}
          icon={<ShieldCheck className="w-5 h-5" />}
          trend="↑ 3 this week"
          trendColor="text-emerald-500"
          iconBg="bg-emerald-500/10"
          iconColor="text-emerald-500"
          sparklineData={sparklineData2}
          sparklineColor="#34D399"
        />
        <StatCard
          title="Disabled Workspaces"
          value={stats.disabledWorkspaces}
          icon={<AlertCircle className="w-5 h-5" />}
          trend="— 0 this week"
          trendColor="text-muted-foreground"
          iconBg="bg-rose-500/10"
          iconColor="text-rose-500"
          sparklineData={sparklineData3}
          sparklineColor="#F87171"
        />
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon={<Users className="w-5 h-5" />}
          trend="↑ 12 this week"
          trendColor="text-emerald-500"
          iconBg="bg-blue-500/10"
          iconColor="text-blue-500"
          sparklineData={sparklineData4}
          sparklineColor="#60A5FA"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 p-6 border border-border rounded-2xl shadow-xs bg-card flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-foreground text-[15px]">Workspace Activity</h3>
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg bg-card text-[12px] font-bold text-muted-foreground hover:bg-muted transition">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                Last 7 Days
                <svg className="w-3.5 h-3.5 text-muted-foreground ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </button>
              <button className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition">
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-6 mb-4">
             <div className="flex items-center gap-2">
               <div className="w-2.5 h-2.5 rounded-full bg-primary"></div>
               <span className="text-[12px] font-bold text-muted-foreground">Active Workspaces</span>
             </div>
             <div className="flex items-center gap-2">
               <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
               <span className="text-[12px] font-bold text-muted-foreground">New Workspaces</span>
             </div>
          </div>
          <div className="flex-1 min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={activityData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-border" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'currentColor', fontWeight: 600 }} dy={10} className="text-muted-foreground" />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'currentColor', fontWeight: 600 }} className="text-muted-foreground" />
                <Tooltip contentStyle={{ borderRadius: '12px', background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.15)' }} />
                <Line type="monotone" dataKey="active" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 4, fill: '#8B5CF6', strokeWidth: 2, stroke: 'hsl(var(--card))' }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="new" stroke="#3B82F6" strokeWidth={2} dot={{ r: 4, fill: '#3B82F6', strokeWidth: 2, stroke: 'hsl(var(--card))' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 border border-border rounded-2xl shadow-xs bg-card flex flex-col">
          <h3 className="font-bold text-foreground text-[15px] mb-6">Workspaces by Status</h3>
          <div className="flex-1 flex flex-col items-center justify-center relative">
            <div className="h-[200px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={85}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {donutData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--foreground))', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.15)' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-4">
              <span className="text-3xl font-bold text-foreground">{stats.totalWorkspaces}</span>
              <span className="text-[12px] font-bold text-muted-foreground">Total</span>
            </div>
            
            <div className="w-full mt-4 space-y-3">
              {donutData.map(item => {
                const percentage = stats.totalWorkspaces > 0 ? Math.round((item.value / stats.totalWorkspaces) * 100) : 0;
                return (
                  <div key={item.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-[13px] font-bold text-foreground">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[13px]">
                      <span className="font-bold text-foreground">{item.value}</span>
                      <span className="text-muted-foreground font-medium">({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border rounded-2xl shadow-xs bg-card overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h3 className="font-bold text-foreground text-[15px]">Recent Workspaces</h3>
            <button className="text-[13px] font-bold text-primary hover:underline transition">View all</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-muted/40 border-b border-border text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-bold text-[12px]">Workspace</th>
                  <th className="px-6 py-3 font-bold text-[12px]">Slug</th>
                  <th className="px-6 py-3 font-bold text-[12px]">Created</th>
                  <th className="px-6 py-3 font-bold text-[12px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recentWorkspaces.map(workspace => (
                  <tr key={workspace.id} className="hover:bg-muted/30 transition">
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-[13px] shrink-0">
                          {workspace.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-bold text-foreground text-[13px]">{workspace.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-[13px] text-muted-foreground font-medium">{workspace.slug}</span>
                    </td>
                    <td className="px-6 py-3 text-[13px] text-muted-foreground font-medium">
                      {new Date(workspace.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide uppercase ${
                        workspace.status === 'ACTIVE' 
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' 
                          : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
                      }`}>
                        {workspace.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {recentWorkspaces.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground font-medium text-[13px]">
                      No recent workspaces found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-6 border border-border rounded-2xl shadow-xs bg-card flex flex-col">
           <div className="flex items-center justify-between mb-6">
             <h3 className="font-bold text-foreground text-[15px]">System Status</h3>
             <button className="text-[13px] font-bold text-primary hover:underline transition">View details</button>
           </div>
           <div className="space-y-4 flex-1">
              <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span className="font-bold text-foreground text-[13px]">API Services</span>
                </div>
                <span className="text-[11px] bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-md font-bold uppercase tracking-wide">Operational</span>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <span className="font-bold text-foreground text-[13px]">WebSocket Gateway</span>
                </div>
                <span className="text-[11px] bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 px-2 py-1 rounded-md font-bold uppercase tracking-wide">Operational</span>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  trend: string;
  trendColor: string;
  iconBg: string;
  iconColor: string;
  sparklineData: any[];
  sparklineColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, trend, trendColor, iconBg, iconColor, sparklineData, sparklineColor }) => (
  <div className="p-5 border border-border rounded-2xl bg-card shadow-xs flex flex-col relative overflow-hidden group">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center border border-border/40`}>
          {icon}
        </div>
        <div>
          <p className="text-[12px] font-bold text-muted-foreground mb-0.5">{title}</p>
          <div className="text-2xl font-bold text-foreground leading-none">{value}</div>
        </div>
      </div>
    </div>
    <div className="flex items-end justify-between mt-auto">
      <div className={`text-[12px] font-bold ${trendColor}`}>
        {trend}
      </div>
      <div className="w-24 h-8 ml-auto">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sparklineData}>
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={sparklineColor} 
              strokeWidth={2} 
              dot={false} 
              isAnimationActive={false} 
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
);

